import React, { useCallback, useState } from 'react';
import { Dialog } from '@mui/material';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { ProjectForm } from './projectForm';
import { addNewProject } from '../../services/api/projectService';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import useAppSnackbar from '../../hooks/useAppSnackbar';

interface Props {
  onCloseAddDialog(): void;
  isOpen: boolean;
  onRequestSuccessful(): void;
}

export function ProjectDialogAdd({
  onCloseAddDialog,
  isOpen,
  onRequestSuccessful,
}: Props): JSX.Element {
  const { onErrorSnackbar } = useAppSnackbar();

  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState<boolean>(false);

  const handleCloseProjects = useCallback(() => {
    onCloseAddDialog();
    setIsOpenConfirmation(false);
  }, [onCloseAddDialog]);

  const handleAddNewProject = useCallback(
    (newProject) => {
      setIsLoadingRequest(true);
      addNewProject({
        code: newProject.code,
        name:
          typeof newProject.name === 'string'
            ? newProject.name
            : newProject.name.label,
        ongoing: newProject.ongoing,
        projectOwnerId: newProject.projectOwner.id,
        salesManagerId: newProject.budgetOwner.id,
        clientId: newProject.client.id,
        transferIndex: newProject.transferIndex,
        responsibleClientName: newProject.responsibleClientName,
        responsibleClientEmail: newProject.responsibleClientEmail,
        isAMS: newProject.isAMS,
        towerId: newProject.tower.id,
      })
        .then(() => {
          handleCloseProjects();
          onRequestSuccessful();
          setIsOpenConfirmation(true);
        })
        .then(() => {
          setTimeout(() => {
            setIsOpenConfirmation(false);
          }, 1000);
        })
        .catch((error) => {
          return onErrorSnackbar(error as Error);
        })
        .finally(() => setIsLoadingRequest(false));
    },
    [handleCloseProjects, onErrorSnackbar, onRequestSuccessful]
  );

  return (
    <>
      <Dialog
        open={isOpen}
        sx={{ borderRadius: '5px' }}
        PaperProps={{
          style: { margin: '16px' },
        }}
      >
        <AppDialogTitle title="Add Project" handleClose={handleCloseProjects} />
        <ProjectForm
          onSubmit={handleAddNewProject}
          onCancel={handleCloseProjects}
          isLoading={isLoadingRequest}
        />
      </Dialog>
      <AppConfirmationSuccessful
        isOpen={isOpenConfirmation}
        message="Project added"
        onClose={handleCloseProjects}
      />
    </>
  );
}
